import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PartnerDealBriefingVersionResponse } from '@interfaces/partners/responses/partner-deal-briefing.response';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { Subscription, first } from 'rxjs';

/** Situação da visualização de uma versão. */
export type BriefingPreviewStatus = 'loading' | 'ready' | 'waiting' | 'error' | 'unavailable';

/** O que a tela precisa para desenhar a visualização de uma versão. */
export interface BriefingPreviewView {
  status: BriefingPreviewStatus;
  /**
   * O último HTML carregado desta versão. Fica na tela enquanto um rascunho mais novo carrega, para a visualização
   * não piscar a cada render do agente.
   */
  html: SafeHtml | null;
  /** Mensagem do backend (502, pronta sem conteúdo) ou o texto da tela. */
  message: string | null;
}

interface PreviewEntry {
  /** O HTML mais novo já carregado desta versão. */
  html: SafeHtml | null;
  /** `versionId|previewUpdatedAt` a que o status se refere. */
  statusKey: string;
  status: BriefingPreviewStatus;
  message: string | null;
}

const IDLE_VIEW: BriefingPreviewView = { status: 'loading', html: null, message: null };

const ERROR_FALLBACK =
  'A visualização do briefing não pôde ser montada agora. Use Tentar de novo; numa versão pronta, Baixar PDF continua funcionando.';

/** Campos do envelope de erro da rota `.../html` (contrato do Q3). */
const NO_DRAFT_FIELD = 'preview';

/**
 * Visualização em HTML do Briefing (card 3878, Q3): busca, guarda e descreve o HTML de cada versão.
 *
 * - Cache por versão e por `previewUpdatedAt`: cada carga pesa de 1 a 1,5 MB (fontes e Paged.js embutidos), então
 *   só baixa de novo quando o rascunho muda. Guarda só o HTML mais novo de cada versão, e só da versão aberta e da
 *   que está gerando (as outras saem do cache ao trocar de versão).
 * - Uma busca por versão: rascunho novo cancela a busca do anterior, e resposta fora da vez é descartada.
 * - 404 com erro no campo `preview` = "ainda sem rascunho" (ou versão sem conteúdo); qualquer outra falha (502 do
 *   Doc-Generate, rede) vira erro com "Tentar de novo".
 * - `reset()` na troca de deal e ao sair da aba solta tudo; resposta que chega depois é descartada.
 *
 * Provido pela aba (`providers`), então o ciclo de vida acompanha a tela.
 */
@Injectable()
export class BriefingPreviewStore implements OnDestroy {
  private readonly partnersHttp = inject(PartnersHttpService);
  private readonly sanitizer = inject(DomSanitizer);

  private readonly entries = signal<Record<string, PreviewEntry>>({});
  /**
   * A busca em andamento de cada versão, com a chave (`versionId|previewUpdatedAt`) que ela traz. Uma por versão: a
   * busca de um rascunho mais novo cancela a do anterior, e resposta de chave que não é mais a da vez é descartada.
   * Sem isso, o rascunho T1 que chegasse depois do conteúdo final T2 prendia a versão pronta no rascunho velho.
   */
  private readonly requests = new Map<string, { key: string; subscription: Subscription }>();
  private generation = 0;

  /** Visualização de uma versão (lê o signal: use dentro de `computed`/template). */
  view(version: PartnerDealBriefingVersionResponse | null): BriefingPreviewView {
    if (!version) return IDLE_VIEW;
    if (version.status === 'Failed') return { status: 'unavailable', html: null, message: null };

    const entry = this.entries()[version.id];
    if (!version.hasPreview) {
      // Em geração sem rascunho ainda: esqueleto. Mantém o HTML antigo, se houver (não acontece na prática).
      return { status: 'waiting', html: entry?.html ?? null, message: null };
    }
    if (!entry) return IDLE_VIEW;

    const key = previewKey(version);
    if (entry.statusKey !== key) return { status: 'loading', html: entry.html, message: null };
    return { status: entry.status, html: entry.html, message: entry.message };
  }

  /**
   * Garante o HTML da versão no `previewUpdatedAt` atual. Sem rascunho ou já guardado, não faz nada.
   *
   * @param keepVersionIds versões que continuam guardadas além desta (a que está gerando). As demais saem do cache:
   *   cada HTML pesa de 1 a 1,5 MB, e percorrer a lista de versões não pode acumular memória.
   */
  load(
    dealId: string,
    version: PartnerDealBriefingVersionResponse | null,
    keepVersionIds: readonly (string | null | undefined)[] = []
  ): void {
    this.prune([version?.id, ...keepVersionIds]);
    if (!version || version.status === 'Failed' || !version.hasPreview) return;

    const key = previewKey(version);
    const entry = this.entries()[version.id];
    if (entry?.statusKey === key && entry.status !== 'loading') return;
    if (this.requests.get(version.id)?.key === key) return;

    this.fetch(dealId, version.id, key);
  }

  /** "Tentar de novo" depois de um erro: busca de novo o mesmo rascunho. */
  retry(dealId: string, version: PartnerDealBriefingVersionResponse): void {
    if (!version.hasPreview) return;
    this.fetch(dealId, version.id, previewKey(version));
  }

  /** Solta o HTML e as buscas em andamento (troca de deal, saída da aba). */
  reset(): void {
    this.generation++;
    this.requests.forEach(request => request.subscription.unsubscribe());
    this.requests.clear();
    this.entries.set({});
  }

  /** Tira do cache (e cancela a busca de) toda versão fora de `keep`. */
  private prune(keep: readonly (string | null | undefined)[]): void {
    const keepIds = new Set(keep.filter((id): id is string => !!id));
    this.requests.forEach((request, versionId) => {
      if (keepIds.has(versionId)) return;
      request.subscription.unsubscribe();
      this.requests.delete(versionId);
    });
    const entries = this.entries();
    if (Object.keys(entries).every(versionId => keepIds.has(versionId))) return;
    this.entries.set(
      Object.fromEntries(Object.entries(entries).filter(([versionId]) => keepIds.has(versionId)))
    );
  }

  /** A resposta desta busca ainda é a da vez (mesma geração e a chave mais nova pedida para a versão). */
  private isCurrent(generation: number, versionId: string, key: string): boolean {
    return generation === this.generation && this.requests.get(versionId)?.key === key;
  }

  ngOnDestroy(): void {
    this.reset();
  }

  private fetch(dealId: string, versionId: string, key: string): void {
    // Uma busca por versão: a do rascunho anterior é cancelada antes de abrir a nova.
    this.requests.get(versionId)?.subscription.unsubscribe();
    const generation = this.generation;
    this.patch(versionId, { statusKey: key, status: 'loading', message: null });

    const pending = { key, subscription: new Subscription() };
    this.requests.set(versionId, pending);
    pending.subscription = this.partnersHttp
      .getDealBriefingHtml(dealId, versionId)
      .pipe(first())
      .subscribe({
        next: html => {
          if (!this.isCurrent(generation, versionId, key)) return;
          this.requests.delete(versionId);
          if (!html) {
            this.patch(versionId, { statusKey: key, status: 'error', message: ERROR_FALLBACK });
            return;
          }
          // Bypass seguro aqui: o `[srcdoc]` passaria pelo sanitizer do Angular, que tira scripts, estilos e a meta da
          // CSP e quebraria a visualização. O HTML é gerado pelo nosso servidor (Doc-Generate, conteúdo do agente
          // escapado no template Jinja, CSP restritiva na meta) e roda num iframe `sandbox="allow-scripts"` SEM
          // `allow-same-origin`: origem opaca, sem cookie, storage nem acesso à página da plataforma.
          this.patch(versionId, {
            html: this.sanitizer.bypassSecurityTrustHtml(html),
            statusKey: key,
            status: 'ready',
            message: null,
          });
        },
        error: err => {
          if (!this.isCurrent(generation, versionId, key)) return;
          this.requests.delete(versionId);
          const { field, message } = readError(err);
          this.patch(
            versionId,
            field === NO_DRAFT_FIELD
              ? { statusKey: key, status: 'waiting', message }
              : { statusKey: key, status: 'error', message: message ?? ERROR_FALLBACK }
          );
        },
      });
  }

  private patch(versionId: string, changes: Partial<PreviewEntry>): void {
    this.entries.update(entries => {
      const current = entries[versionId] ?? {
        html: null,
        statusKey: '',
        status: 'loading' as BriefingPreviewStatus,
        message: null,
      };
      return { ...entries, [versionId]: { ...current, ...changes } };
    });
  }
}

function previewKey(version: PartnerDealBriefingVersionResponse): string {
  return `${version.id}|${version.previewUpdatedAt ?? ''}`;
}

/** Campo e mensagem do `ErrorResponse` do `VaultApiService`; falha técnica ("Unknown error") vem sem mensagem útil. */
function readError(err: unknown): { field: string | null; message: string | null } {
  const response = err as {
    message?: string;
    errors?: { field?: string | null; message?: string }[];
  } | null;
  const firstError = response?.errors?.[0];
  const field = firstError?.field?.trim().toLowerCase() || null;
  const message =
    response?.message === 'Unknown error' ? null : firstError?.message?.trim() || null;
  return { field, message };
}
